import * as Sentry from '@sentry/react';
import { reactRouterBrowserTracingIntegration } from '@sentry/react/react-router';
import { sanitizeBreadcrumb, sanitizeErrorEvent, sanitizeTransactionEvent, siteAssetUrl } from './privacy';

export interface MonitoringEnvironment {
  PROD?: boolean;
  MODE?: string;
  VITE_ENABLE_MOCKS?: string;
  VITE_SENTRY_ENABLED?: string;
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_SENTRY_RELEASE?: string;
  VITE_SENTRY_TRACES_SAMPLE_RATE?: string;
  VITE_API_URL?: string;
}

export const shouldEnableMonitoring = (
  env: MonitoringEnvironment,
  userAgent: string,
): boolean => env.PROD === true && env.MODE === 'production'
  && env.VITE_SENTRY_ENABLED === 'true' && Boolean(env.VITE_SENTRY_DSN?.trim())
  && env.VITE_ENABLE_MOCKS !== 'true'
  && !/ReactSnap|HeadlessChrome/i.test(userAgent);

export const initMonitoring = (): void => {
  if (!shouldEnableMonitoring(import.meta.env, navigator.userAgent)) return;
  try {
    Sentry.init(monitoringOptions(import.meta.env));
  } catch {
    // Monitoring configuration must never prevent the marketplace from starting.
    console.warn('Error monitoring could not be initialized');
  }
};

/** Sampling rate in [0, 1]; anything missing or invalid means 0 (no performance data). */
export const parseTracesSampleRate = (value: string | undefined): number => {
  const rate = Number(value);
  return value?.trim() && Number.isFinite(rate) ? Math.min(1, Math.max(0, rate)) : 0;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Trace headers (`sentry-trace`, `baggage`) go to the Bricolat API only. The API is served from the same origin
 * (`/api/v1`) in production; an absolute `VITE_API_URL` is honoured too. Third-party domains never match.
 */
export const tracePropagationTargets = (apiUrl: string | undefined, origin = globalThis.location?.origin): RegExp[] => {
  const base = (apiUrl?.trim() || '/api/v1').replace(/\/+$/, '');
  if (/^https?:\/\//i.test(base)) return [new RegExp(`^${escapeRegExp(base)}(?:/|$)`)];
  const path = escapeRegExp(base.startsWith('/') ? base : `/${base}`);
  return [
    new RegExp(`^${path}(?:/|$)`),
    ...(origin ? [new RegExp(`^${escapeRegExp(origin)}${path}(?:/|$)`)] : []),
  ];
};

export const monitoringOptions = (env: MonitoringEnvironment): Sentry.BrowserOptions => {
  const tracesSampleRate = parseTracesSampleRate(env.VITE_SENTRY_TRACES_SAMPLE_RATE);
  return {
    dsn: env.VITE_SENTRY_DSN,
    environment: env.VITE_SENTRY_ENVIRONMENT || 'production',
    release: env.VITE_SENTRY_RELEASE,
    // Client IP (inferred by Sentry from the connection: sdk.settings.infer_ip = 'auto'), user id and role are the diagnostic identity; cookies, headers, bodies and query
    // strings stay off, and the exclusion filter in privacy.ts is a second line of defence.
    dataCollection: {
      userInfo: true,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      genAI: { inputs: false, outputs: false },
      stackFrameVariables: false,
      frameContextLines: 0,
    },
    defaultIntegrations: false,
    integrations: [
      Sentry.eventFiltersIntegration(),
      Sentry.dedupeIntegration(),
      Sentry.globalHandlersIntegration(),
      Sentry.browserApiErrorsIntegration(),
      Sentry.httpContextIntegration(),
      Sentry.consoleIntegration(),
      Sentry.breadcrumbsIntegration({ dom: true, fetch: true, history: true, xhr: true, sentry: false }),
      // Performance is opt-in: without a positive rate the tracing integration is not even registered.
      ...(tracesSampleRate > 0 ? [reactRouterBrowserTracingIntegration({ enableInp: true })] : []),
    ],
    allowUrls: [siteAssetUrl],
    // Classic transactions (not streamed spans) so beforeSendTransaction can strip URL params.
    traceLifecycle: 'static',
    tracesSampleRate,
    tracePropagationTargets: tracePropagationTargets(env.VITE_API_URL),
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    beforeSend: sanitizeErrorEvent,
    beforeSendTransaction: sanitizeTransactionEvent,
    beforeBreadcrumb: sanitizeBreadcrumb,
  };
};

const capturedErrors = new WeakSet<Error>();
export const captureAppError = (error: unknown): void => {
  if (!Sentry.getClient()) return;
  // Never serialize response/config objects from Axios or arbitrary promise values.
  if (error instanceof Error && !capturedErrors.has(error)) {
    capturedErrors.add(error);
    Sentry.captureException(error);
  }
};

/** Attach the signed-in account (internal id and role only, never email or name); `null` clears it. */
export const setMonitoringUser = (user: { id: string | number; role?: string } | null): void => {
  Sentry.setUser(user ? { id: String(user.id), role: user.role } : null);
};

const HYDRATION_SAMPLE_RATE = 0.1;
const reportedHydrationRoutes = new Set<string>();

const LOCALE_PREFIX = /^\/(ar|en|darija|darija-ar)(?=\/|$)/;
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

/**
 * Route used for the Sentry tag, fingerprint and dedupe: locale prefix split off (kept as its own tag),
 * ids and slugs replaced by placeholders so one template is one issue, query and hash dropped.
 */
export const normalizeHydrationRoute = (pathname: string): { route: string; locale: string } => {
  let path = pathname.split(/[?#]/)[0] || '/';
  const locale = path.match(LOCALE_PREFIX)?.[1] ?? 'fr';
  path = path.replace(LOCALE_PREFIX, '') || '/';
  path = path
    .replace(new RegExp(`^/providers/${UUID}(?=/|$)`, 'i'), '/providers/:id')
    .replace(/^\/blog\/[^/]+(?=\/|$)/, '/blog/:slug')
    .replace(new RegExp(`/${UUID}(?=/|$)`, 'gi'), '/:id');
  return { route: path.length > 1 ? path.replace(/\/+$/, '') : path, locale };
};

/**
 * Hydration mismatches are reported with their route and one fingerprint per route, sampled at 10 %
 * (a regression hits every visitor of the route, so a sample is enough and keeps the quota safe).
 */
export const captureHydrationMismatch = (error: unknown, rawRoute: string, random: () => number = Math.random): void => {
  if (!Sentry.getClient() || random() >= HYDRATION_SAMPLE_RATE) return;
  const { route, locale } = normalizeHydrationRoute(rawRoute);
  if (reportedHydrationRoutes.has(route)) return;
  reportedHydrationRoutes.add(route);
  const message = error instanceof Error ? error.message : String(error);
  Sentry.withScope((scope) => {
    scope.setTag('route', route);
    scope.setTag('locale', locale);
    scope.setFingerprint(['hydration-mismatch', route]);
    Sentry.captureMessage(`hydration-mismatch ${route}: ${message.slice(0, 200)}`, 'error');
  });
};
